import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faArrowDown, faChevronDown, faChevronUp, faSignInAlt, faSitemap, faTimes, } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BButton, BCard, BCardBody, BCardHeader } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, onUnmounted, ref, watch } from "vue";
import { useDatatypesMapper } from "@/composables/datatypesMapper";
import { useInvocationGraph } from "@/composables/useInvocationGraph";
import { useWorkflowStateStore } from "@/stores/workflowEditorStateStore";
import Heading from "@/components/Common/Heading.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
import WorkflowGraph from "@/components/Workflow/Editor/WorkflowGraph.vue";
import WorkflowInvocationStep from "@/components/WorkflowInvocationState/WorkflowInvocationStep.vue";
import WorkflowInvocationStepHeader from "@/components/WorkflowInvocationState/WorkflowInvocationStepHeader.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'InvocationGraph',
    props: {
        invocation: null,
        workflow: null,
        isTerminal: { type: Boolean },
        zoom: { default: 0.75 },
        showMinimap: { type: Boolean, default: true },
        showZoomControls: { type: Boolean, default: true },
        initialX: { default: -40 },
        initialY: { default: -40 },
        isFullPage: { type: Boolean, default: false }
    },
    setup(__props) {
        const props = __props;
        library.add(faArrowDown, faChevronDown, faChevronUp, faSignInAlt, faSitemap, faTimes);
        const loadingGraph = ref(true);
        const initialLoading = ref(true);
        const errored = ref(false);
        const errorMessage = ref("");
        const pollTimeout = ref(null);
        const stepCard = ref(null);
        const loadedJobInfo = ref(null);
        const invocationRef = computed(() => props.invocation);
        const { datatypesMapper } = useDatatypesMapper();
        const workflowId = computed(() => props.workflow?.id);
        const workflowVersion = computed(() => props.workflow?.version);
        const { steps, storeId, loadInvocationGraph } = useInvocationGraph(invocationRef, workflowId.value, workflowVersion.value);
        // Equivalent to onMounted; this is where the graph is initialized, and the polling is started
        watch(() => workflowId.value, async (wfId) => {
            if (wfId) {
                await pollInvocationGraph();
            }
        }, { immediate: true });
        const stateStore = useWorkflowStateStore(storeId.value);
        const { activeNodeId } = storeToRefs(stateStore);
        watch(() => props.zoom, () => (stateStore.scale = props.zoom), { immediate: true });
        onUnmounted(() => {
            clearTimeout(pollTimeout.value);
        });
        const initialPosition = computed(() => ({
            x: -props.initialX * props.zoom,
            y: -props.initialY * props.zoom,
        }));
        /** Updates and loads the invocation graph */
        async function loadGraph() {
            loadingGraph.value = true;
            errored.value = false;
            errorMessage.value = "";
            try {
                await loadInvocationGraph();
                errored.value = false;
            }
            catch (error) {
                if (error.response?.data.err_msg) {
                    errorMessage.value = error.response.data.err_msg;
                }
                else {
                    errorMessage.value = error;
                }
                errored.value = true;
            }
            finally {
                loadingGraph.value = false;
                if (initialLoading.value) {
                    initialLoading.value = false;
                }
            }
        }
        /** Poll and load the invocation graph until the invocation is terminal */
        async function pollInvocationGraph() {
            await loadGraph();
            if (!props.isTerminal) {
                pollTimeout.value = setTimeout(pollInvocationGraph, 3000);
            }
            else {
                clearTimeout(pollTimeout.value);
            }
        }
        function scrollStepToView() {
            const stepCardHeader = stepCard.value?.querySelector(".card-header");
            stepCardHeader?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        /** On a repetition of the step clicked, scroll to the step */
        function stepClicked(nodeId) {
            if (nodeId === activeNodeId.value) {
                scrollStepToView();
            }
        }
        return { __sfc: true, props, loadingGraph, initialLoading, errored, errorMessage, pollTimeout, stepCard, loadedJobInfo, invocationRef, datatypesMapper, workflowId, workflowVersion, steps, storeId, loadInvocationGraph, stateStore, activeNodeId, initialPosition, loadGraph, pollInvocationGraph, scrollStepToView, stepClicked, faArrowDown, faTimes, FontAwesomeIcon, BAlert, BButton, BCard, BCardBody, BCardHeader, Heading, LoadingSpan, WorkflowGraph, WorkflowInvocationStep, WorkflowInvocationStepHeader };
    }
});
